/**
 * Loosely-shaped ICE server entry — used specifically where the data may come from an
 * external, not-fully-trusted source (the TURN-credentials JSON payload), unlike the
 * DOM's `RTCIceServer` (required `urls`) used for the session-config entries this
 * file resolves.
 */
interface IceServerEntry {
  urls?: string | string[];
  username?: string;
  credential?: string;
}

export function normalizeUrls(urls: string | string[] | undefined): string[] {
  if (urls == null) return [];
  if (Array.isArray(urls)) return urls.map(String).filter(Boolean);
  const value = String(urls).trim();
  return value ? [value] : [];
}

export function isTurnUrls(urls: string | string[] | undefined): boolean {
  return normalizeUrls(urls).some((url) => /^turns?:/i.test(url));
}

interface TurnCredentials {
  username: string;
  credential: string;
}

export function extractTurnCredentials(
  iceServers: IceServerEntry[] | undefined
): TurnCredentials | null {
  if (!Array.isArray(iceServers)) return null;
  for (const entry of iceServers) {
    if (!entry || typeof entry !== "object") continue;
    if (!isTurnUrls(entry.urls)) continue;
    const username = entry.username != null ? String(entry.username) : "";
    const credential = entry.credential != null ? String(entry.credential) : "";
    if (username && credential) {
      return { username, credential };
    }
  }
  return null;
}

export function applyTurnCredentials(
  entry: IceServerEntry,
  cred: TurnCredentials | null
): IceServerEntry {
  if (!cred || !isTurnUrls(entry?.urls)) return entry;
  return {
    ...entry,
    username: cred.username,
    credential: cred.credential,
  };
}

/**
 * Session config fields the merge below reads/writes. Also doubles, via a plain cast,
 * as the shape of the /turn-credentials endpoint response — `resolveSessionConfig`
 * doesn't redeclare that shape separately since it's structurally the same thing.
 */
interface IceServersConfig {
  iceServers?: IceServerEntry[];
  iceTransportPolicy?: string;
}

/**
 * Merge ephemeral TURN credentials from /turn-credentials into base iceServers.
 */
export function mergeTurnCredentials<T extends IceServersConfig>(
  config: T,
  payload: IceServersConfig | null | undefined
): T {
  if (!payload || typeof payload !== "object") return config;

  const base = Array.isArray(config.iceServers) ? config.iceServers : [];
  const cred = extractTurnCredentials(Array.isArray(payload.iceServers) ? payload.iceServers : []);

  let iceServers: IceServerEntry[];
  if (base.length > 0) {
    iceServers = cred
      ? base.map((entry) => applyTurnCredentials(entry, cred))
      : base.map((entry) => ({ ...entry }));
  } else if (Array.isArray(payload.iceServers) && payload.iceServers.length > 0) {
    iceServers = payload.iceServers.map((entry) => ({ ...entry }));
  } else {
    return config;
  }

  // `T`'s own `iceServers`/`iceTransportPolicy` fields may be narrower than the generic
  // `IceServersConfig` shape (e.g. `RawSessionConfig.iceServers: RTCIceServer[]`, every
  // entry always has `urls`). That narrowing is genuinely true at runtime here — every
  // branch above only copies/augments entries that already came from a `T`, never
  // fabricates a bare one — but the spread below can't reprove it structurally, hence
  // the one targeted cast.
  const next = { ...config, iceServers } as T;

  // Honor the credential endpoint's policy; without it retain the configured policy.
  const policy =
    typeof payload.iceTransportPolicy === "string"
      ? payload.iceTransportPolicy
      : typeof config.iceTransportPolicy === "string"
        ? config.iceTransportPolicy
        : undefined;
  if (policy) {
    next.iceTransportPolicy = policy as T["iceTransportPolicy"];
  }

  return next;
}

/** Runtime session config the app passes to createTeleopSession (relevant fields only). */
export interface ResolvedSessionConfig {
  signalingWsBase?: string;
  iceServers: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  diagnosticsWire?: boolean;
  controlAuthRequired?: boolean;
  iceCredentialUrl?: string;
  iceRefreshAt?: number;
}

/** `ResolvedSessionConfig` before ephemeral TURN credentials are resolved/merged in. */
export interface RawSessionConfig extends ResolvedSessionConfig {
  iceCredentialUrl?: string;
}

interface ResolveSessionConfigOptions {
  fetchImpl?: typeof fetch;
}

/**
 * Resolve a full runtime session config from the base config loaded from `/config.json`.
 *
 * This is the ONLY entry the app needs for config assembly: it transparently resolves
 * ephemeral TURN credentials from the configured credentials URL and merges them into the
 * ICE server list. The app never needs to know about ICE/TURN/WebRTC internals.
 */
export async function resolveSessionConfig(
  baseConfig: RawSessionConfig,
  opts: ResolveSessionConfigOptions = {}
): Promise<ResolvedSessionConfig> {
  const cfg: RawSessionConfig = { ...baseConfig };
  const credentialUrl = cfg.iceCredentialUrl;
  if (typeof credentialUrl !== "string" || !credentialUrl.trim()) {
    return cfg;
  }
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const res = await fetchImpl(credentialUrl, { cache: "no-store", signal: AbortSignal.timeout(10_000) });
  if (!res.ok) throw new Error(`ice credentials: HTTP ${res.status}`);
  // res.json() is `any` per lib.dom.d.ts (an untypeable network boundary) — contained to
  // this one line; mergeTurnCredentials itself accepts the real, narrow payload type.
  const payload: unknown = await res.json();
  const credentials = payload as IceServersConfig;
  if (!credentials || !Array.isArray(credentials.iceServers) || !credentials.iceServers.length ||
      !credentials.iceServers.every((server) => server && normalizeUrls(server.urls).length &&
        normalizeUrls(server.urls).every((url) => /^(stun|stuns|turn|turns):/.test(url)) &&
        (!isTurnUrls(server.urls) || (server.username && server.credential))) ||
      (credentials.iceTransportPolicy != null && !["all", "relay"].includes(credentials.iceTransportPolicy))) {
    throw new Error("Invalid ICE credentials response");
  }
  const merged = mergeTurnCredentials(cfg, payload as IceServersConfig);
  // Keep the endpoint so every reconnect can obtain new credentials.
  merged.iceRefreshAt = iceCredentialRefreshAt(payload as IceServersConfig);

  return merged;
}

export function iceCredentialRefreshAt(payload: IceServersConfig & { ttl?: number; expiresAt?: number }, now = Date.now()): number {
  const deadlines: number[] = [];
  if (Number.isFinite(payload.ttl)) deadlines.push(now + payload.ttl! * 1000);
  if (Number.isFinite(payload.expiresAt)) deadlines.push(payload.expiresAt! * 1000);
  for (const server of payload.iceServers ?? []) {
    const prefix = String(server.username ?? "").split(":")[0] ?? "";
    if (/^\d+$/.test(prefix)) deadlines.push(Number(prefix) * 1000);
  }
  const expiry = deadlines.length ? Math.min(...deadlines) : now + 600_000;
  const remaining = expiry - now;
  if (remaining < 60_000) throw new Error("TURN credentials expired or too close to expiry");
  return expiry - Math.min(60_000, remaining / 5);
}
